import { ButtonLink } from "design-system/atoms/Button"
import { Card } from "design-system/molecules/Card"
import { MetricCard } from "design-system/organisms/insights/MetricCard"
import { formatNumber, formatPercent, isNil } from "lopermut"
import { ArrowUpRight, MoveRight } from "lucide-react"
import { useCampaignsImpactQuery } from "modules/campaigns/hooks/useCampaignsImpactQuery"
import {
  useCustomersMetricQuery,
  useInsightsCapturedMetricQuery,
} from "modules/dashboard/hooks/useMetricsQueries"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { CampaignImpactChart } from "../../List/CampaignImpactChart"

type Props = {
  campaignId: string
}

export const CampaignDetailActivity = ({ campaignId }: Props) => {
  const { t } = useTranslation()

  const { data: customers, isPending: isCustomersPending } = useCustomersMetricQuery({
    query: { campaignId },
  })
  const { data: insightsCaptured, isPending: isInsightsPending } = useInsightsCapturedMetricQuery({
    query: { campaignId },
  })

  const [activeMetricIdx, setActiveMetricIdx] = useState(0)
  const { data: campaignsImpactData, isPending: isCampaignsImpactPending } =
    useCampaignsImpactQuery({ query: { campaignId } })

  const engamentPercentage =
    customers?.impacted.total && customers?.impacted.total > 0
      ? ((customers?.engaged.total ?? 0) / customers?.impacted.total) * 100
      : null

  const conversionPercentage =
    customers?.engaged.total && customers?.engaged.total > 0
      ? ((customers?.achieved.total ?? 0) / customers?.engaged.total) * 100
      : null

  // Determine which dataKey to use based on active metric
  const getImpactDataKey = () => {
    if (activeMetricIdx === 0) return "reached"
    if (activeMetricIdx === 1) return "engaged"
    if (activeMetricIdx === 2) return "achieved"
    if (activeMetricIdx === 3) return "insights"

    return "reached"
  }

  const getColorOverride = () => {
    if (activeMetricIdx === 0) return "var(--chart-secondary)"
    if (activeMetricIdx === 1) return "var(--chart-primary)"
    if (activeMetricIdx === 2) return "var(--chart-3)"
    if (activeMetricIdx === 3) return "var(--chart-4)"
  }

  return (
    <Card className="flex flex-col w-full min-w-xs p-6 gap-2">
      <div className="grid gap-4 w-full grid-cols-1 lg:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          key="impactedCustomers"
          title={t("campaigns.detail.activity.reachedCustomers.title")}
          tooltip={t("campaigns.detail.activity.reachedCustomers.description")}
          currentValue={formatNumber({
            amount: customers?.impacted.total,
            abvLevel: (customers?.impacted.total ?? 0) > 1000 ? 3 : 0,
          })}
          deltaPercentage={customers?.impacted.deltaPercentage}
          deltaTooltip={t("campaigns.detail.activity.deltaTooltip")}
          onClick={() => setActiveMetricIdx(0)}
          isActive={activeMetricIdx === 0}
          isPending={isCustomersPending}
        />
        <MetricCard
          key="engagedCustomers"
          title={t("campaigns.detail.activity.engagedCustomers.title")}
          tooltip={t("campaigns.detail.activity.engagedCustomers.description")}
          currentValue={formatNumber({
            amount: customers?.engaged.total,
            abvLevel: (customers?.engaged.total ?? 0) > 1000 ? 3 : 0,
          })}
          metric={
            !isNil(engamentPercentage) && (
              <div className="flex flex-row items-center gap-1">
                <MoveRight className="size-4" />
                {t("campaigns.detail.activity.engagedCustomers.percent", {
                  percent: formatPercent({
                    amount: engamentPercentage,
                    fix: 0,
                  }),
                })}
              </div>
            )
          }
          metricTooltip={t("campaigns.detail.activity.engagedCustomers.numberTooltip")}
          onClick={() => setActiveMetricIdx(1)}
          isActive={activeMetricIdx === 1}
          isPending={isCustomersPending}
        />
        <MetricCard
          key="customersAchieved"
          title={t("campaigns.detail.activity.customersAchieved.title")}
          tooltip={t("campaigns.detail.activity.customersAchieved.description")}
          currentValue={formatNumber({
            amount: customers?.achieved.total,
            abvLevel: (customers?.achieved.total ?? 0) > 1000 ? 3 : 0,
          })}
          metric={
            !isNil(conversionPercentage) && (
              <div className="flex flex-row items-center gap-1">
                <MoveRight className="size-4" />
                {t("campaigns.detail.activity.customersAchieved.percent", {
                  percent: formatPercent({
                    amount: conversionPercentage,
                    fix: 0,
                  }),
                })}
              </div>
            )
          }
          metricTooltip={t("campaigns.detail.activity.customersAchieved.numberTooltip")}
          onClick={() => setActiveMetricIdx(2)}
          isActive={activeMetricIdx === 2}
          isPending={isCustomersPending}
        />
        <MetricCard
          key="responsesCaptured"
          title={t("campaigns.detail.activity.responsesCaptured.title")}
          tooltip={t("campaigns.detail.activity.responsesCaptured.description")}
          currentValue={formatNumber({
            amount: insightsCaptured?.total,
            abvLevel: (insightsCaptured?.total ?? 0) > 1000 ? 3 : 0,
          })}
          deltaPercentage={insightsCaptured?.deltaPercentage}
          deltaTooltip={t("campaigns.detail.activity.deltaTooltip")}
          onClick={() => setActiveMetricIdx(3)}
          isActive={activeMetricIdx === 3}
          isPending={isInsightsPending}
        />
      </div>
      <CampaignImpactChart
        containerClassName="h-[240px]"
        data={campaignsImpactData}
        isPending={isCampaignsImpactPending}
        showLegend={false}
        colorOverride={getColorOverride()}
        dataKey={getImpactDataKey()}
        action={
          activeMetricIdx === 3 ? (
            <ButtonLink
              to="/campaigns/$id/activity"
              params={{ id: campaignId }}
              search={{ section: "responses" }}
              variant="ghost"
            >
              {t("campaigns.detail.viewResponses")}
              <ArrowUpRight />
            </ButtonLink>
          ) : (
            <ButtonLink
              to="/conversations/list"
              search={{ filterBy: { campaignId } }}
              variant="ghost"
            >
              {t("campaigns.detail.viewConversations")}
              <ArrowUpRight />
            </ButtonLink>
          )
        }
      />
    </Card>
  )
}
